import NextLink from 'next/link';
import { useState } from 'react';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import Divider from '@material-ui/core/Divider';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import GradeIcon from '@material-ui/icons/Grade';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import AssessmentIcon from '@material-ui/icons/Assessment';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';
import ClassIcon from '@material-ui/icons/Class';
import GroupAddIcon from '@material-ui/icons/GroupAdd';
import HomeIcon from '@material-ui/icons/Home';
import NotificationsIcon from '@material-ui/icons/Notifications';
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import ListAltIcon from '@material-ui/icons/ListAlt';
import VisibilityIcon from '@material-ui/icons/Visibility';
import EqualizerIcon from '@material-ui/icons/Equalizer';
import AssignmentIcon from '@material-ui/icons/Assignment';
import TocIcon from '@material-ui/icons/Toc';
import PostAddIcon from '@material-ui/icons/PostAdd';
import EventSeatIcon from '@material-ui/icons/EventSeat';
import PlayCircleOutlineIcon from '@material-ui/icons/PlayCircleOutline';
import i18next from 'i18next';
import { makeStyles } from '@material-ui/core/styles';
import Notifications from '../components/Notifications';
import colours from '../styles/colours';
import useAuthedUser from '../hooks/useAuthedUser';
import LoadingSpinner from '../components/LoadingSpinner';
import NewsFeedList from '../components/NewsFeedList';
import Button from '@material-ui/core/Button';
import NewsFeedModal from '../components/NewsFeedModal';
import useNewsStatus, { useUpdateNewsStatus } from '../hooks/useNewsStatus';
import Badge from '@material-ui/core/Badge';
import EmojiObjectsOutlinedIcon from '@material-ui/icons/EmojiObjectsOutlined';
import SuggestionBoxDialog from '../components/SuggestionBoxDialog';

const useStyles = makeStyles({
  actionCard: {
    transition: 'transform 160ms ease, box-shadow 160ms ease',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 9px 24px rgba(35, 56, 66, 0.12)',
    },
  },
});

function quickActions(user) {
  return [
    {
      label: i18next.t('Manage Aspects'),
      icon: (
        <MenuBookIcon
          fontSize="large"
          style={{ color: colours.powder.red, height: 80, width: 80 }}
        />
      ),
      href: '/aspects',
      visible: ['ADMIN', 'SUPERADMIN'].includes(user.type),
    },
    {
      label: i18next.t('Manage All Groups'),
      icon: (
        <PeopleOutlineIcon
          fontSize="large"
          style={{ color: colours.powder.blue, height: 80, width: 80 }}
        />
      ),
      href: '/cohorts',
      visible: ['ADMIN', 'SUPERADMIN'].includes(user.type),
    },
    {
      label: i18next.t('Score Systems'),
      icon: (
        <GradeIcon
          fontSize="large"
          style={{ color: colours.powder.yellow, height: 80, width: 80 }}
        />
      ),
      href: '/systems',
      visible: ['ADMIN', 'SUPERADMIN'].includes(user.type),
    },
    {
      label: i18next.t('Data Captures'),
      icon: (
        <VisibilityIcon
          fontSize="large"
          style={{ color: colours.powder.green, height: 80, width: 80 }}
        />
      ),
      href: '/captures',
      visible: ['ADMIN', 'SUPERADMIN'].includes(user.type),
    },
    {
      label: i18next.t('Change Password'),
      icon: (
        <VpnKeyIcon
          fontSize="large"
          style={{ color: colours.powder.yellow, height: 80, width: 80 }}
        />
      ),
      href: '/settings',
      visible: ['TEACHER', 'TRUST_USER'].includes(user.type),
    },
    {
      label: i18next.t('Manage Students'),
      icon: (
        <GroupAddIcon
          fontSize="large"
          style={{ color: colours.powder.red, height: 80, width: 80 }}
        />
      ),
      href: '/studentUpload',
      visible: ['TEACHER', 'ADMIN', 'SUPERADMIN'].includes(user.type),
    },
    {
      label: i18next.t('Template View'),
      icon: (
        <AssessmentIcon
          fontSize="large"
          style={{ color: colours.powder.blue, height: 80, width: 80 }}
        />
      ),
      href: '/templates',
      visible: [
        'ADMIN',
        'TEACHER',
        'SUPERADMIN',
        'TRUST_ADMIN',
        'TRUST_USER',
      ].includes(user.type),
    },
    {
      label: i18next.t('Import Results'),
      icon: (
        <AssignmentTurnedInIcon
          fontSize="large"
          style={{ color: colours.powder.yellow, height: 80, width: 80 }}
        />
      ),
      href: '/import-results',
      visible:
        ['ADMIN', 'SUPERADMIN'].includes(user.type) &&
        (user.school.canImportResults || user.type === 'SUPERADMIN'),
    },
    {
      label: i18next.t('Aspects View'),
      icon: (
        <ListAltIcon
          fontSize="large"
          style={{ color: colours.powder.green, height: 80, width: 80 }}
        />
      ),
      href: '/aspectsView',
      visible: ['TEACHER', 'ADMIN', 'SUPERADMIN'].includes(user.type),
    },
    {
      label: i18next.t('Attendance Tools'),
      icon: (
        <EqualizerIcon
          fontSize="large"
          style={{ color: colours.powder.red, height: 80, width: 80 }}
        />
      ),
      href: '/attendance',
      visible:
        user.school.groupcallId ||
        user.school.wondeId ||
        ['National Tests', 'SMID Primary School'].includes(user.school.name) ||
        ['SUPERADMIN'].includes(user.type),
    },
    {
      label: i18next.t('Report Dashboard'),
      icon: (
        <AssignmentIcon
          fontSize="large"
          style={{ color: colours.powder.blue, height: 80, width: 80 }}
        />
      ),
      href: '/report-dashboard',
      visible:
        ['SUPERADMIN'].includes(user.type) || user.school.reportDashEnabled,
    },
    {
      label: i18next.t('AOLE Dashboard'),
      icon: (
        <TocIcon
          fontSize="large"
          style={{ color: colours.powder.yellow, height: 80, width: 80 }}
        />
      ),
      href: '/aole',
      visible:
        ['SUPERADMIN'].includes(user.type) || user.school.aoleDashEnabled,
    },
    {
      label: i18next.t('Export Dashboard'),
      icon: (
        <PostAddIcon
          fontSize="large"
          style={{ color: colours.powder.green, height: 80, width: 80 }}
        />
      ),
      href: '/export-dashboard',
      visible: true,
    },
    {
      label: i18next.t('Seating Plans'),
      icon: (
        <EventSeatIcon
          fontSize="large"
          style={{ color: colours.powder.red, height: 80, width: 80 }}
        />
      ),
      href: '/seating-plans',
      visible: ['TEACHER', 'ADMIN', 'SUPERADMIN'].includes(user.type),
    },
    {
      label: i18next.t('Tutorials'),
      icon: (
        <PlayCircleOutlineIcon
          fontSize="large"
          style={{ color: colours.powder.blue, height: 80, width: 80 }}
        />
      ),
      href: '/tutorials',
      visible: true,
    },
  ];
}

function ActionCard({ label, icon, href }) {
  const classes = useStyles();
  const accent = icon.props.style.color;

  return (
    <NextLink href={href} passHref legacyBehavior>
      <Card
        variant="outlined"
        className={classes.actionCard}
        style={{
          height: '100%',
          cursor: 'pointer',
          borderTop: `5px solid ${accent}`,
        }}
      >
        <CardContent
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 25,
            userSelect: 'none',
          }}
        >
          {icon}
          <Typography
            component="h2"
            variant="h6"
            style={{ fontWeight: 800, marginTop: 10, textAlign: 'center' }}
          >
            {label}
          </Typography>
        </CardContent>
      </Card>
    </NextLink>
  );
}

export default function HomePage() {
  const user = useAuthedUser();
  const [isNewsOpen, setNewsOpen] = useState(false);
  const [isSuggestionBoxOpen, setSuggestionBoxOpen] = useState(false);
  const newsStatus = useNewsStatus();
  const updateNewsStatus = useUpdateNewsStatus();
  const unreadBoardCount = newsStatus.data?.unreadCount || 0;

  const openMessageBoard = async () => {
    setNewsOpen(true);
    const nowIso = new Date().toISOString();
    updateNewsStatus.mutate(nowIso);
  };

  if (user.isLoading) return <LoadingSpinner />;

  if (user.isError) return <p>Error: {user.error.message}</p>;

  return (
    <div>
      <main>
        <Typography>
          Welcome to {user.data.school.name} <strong>{user.data.email}</strong>
        </Typography>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
            marginTop: 20,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <HomeIcon fontSize="large" />
            <Typography
              component="h2"
              variant="h4"
              style={{ fontWeight: 900, marginLeft: 10 }}
            >
              Dashboard
            </Typography>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 8,
            }}
          >
            <Button
              variant="outlined"
              color="primary"
              startIcon={<EmojiObjectsOutlinedIcon />}
              onClick={() => setSuggestionBoxOpen(true)}
            >
              Suggestion Box
            </Button>
            {['ADMIN', 'SUPERADMIN'].includes(user.data.type) && (
              <Badge
                color="secondary"
                badgeContent={unreadBoardCount}
                invisible={!unreadBoardCount}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={openMessageBoard}
                >
                  Message Board
                </Button>
              </Badge>
            )}
          </div>
        </div>
        <Divider style={{ marginTop: 10, marginBottom: 20 }} />
        <Grid container spacing={2}>
          {quickActions(user.data).map(
            ({ label, icon, href, visible }) =>
              visible && (
                <Grid item xs={6} sm={4} md={3} key={label}>
                  <ActionCard label={label} icon={icon} href={href} />
                </Grid>
              )
          )}
        </Grid>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginTop: 20,
          }}
        >
          <NotificationsIcon fontSize="large" />
          <Typography
            component="h2"
            variant="h4"
            style={{ fontWeight: 900, marginLeft: 10 }}
          >
            Your Notifications
          </Typography>
        </div>
        <Divider style={{ marginTop: 10, marginBottom: 20 }} />
        <Notifications />
        {['ADMIN', 'SUPERADMIN'].includes(user.data.type) && (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginTop: 20,
              }}
            >
              <Typography
                component="h2"
                variant="h4"
                style={{ fontWeight: 900 }}
              >
                Message Board
              </Typography>
            </div>
            <Divider style={{ marginTop: 10, marginBottom: 20 }} />
            <NewsFeedList />
          </>
        )}
        {isNewsOpen && (
          <NewsFeedModal open={isNewsOpen} onClose={() => setNewsOpen(false)} />
        )}
        <SuggestionBoxDialog
          open={isSuggestionBoxOpen}
          handleClose={() => setSuggestionBoxOpen(false)}
        />
      </main>
    </div>
  );
}
